import { DEEP_GREEN, INCIDENT_ACCENT, INK, MUTED } from '../colorTokens';
import { forReaders } from './redactInternal';

// Every sentence the Incident Remediation page shows. Pure functions, no React, no calculation: the numbers
// come from incidentRemediationReport.js and are only worded here, so the wording can change without touching
// the counting.
//
// Sentences are lists of parts: { text, bold, color }. TextParts (ReportSections.jsx) renders them.
// The notes for open problems (problemNote) name the assignment group and the assigned person when set, and
// they hide server names and addresses found in ServiceNow text (redactInternal.js). That is display-only:
// the detail table and the API show the raw text.

const textPart = (text, options = {}) => ({
  text,
  bold: !!options.bold,
  color: options.color || INK
});

const plural = (count, one, many) => (count === 1 ? one : many);

/** "a", "a and b", "a, b and c" as parts. render(item) returns the parts for one item. */
const listParts = (items, render) => items.flatMap((item, index) => {
  const separator = index === 0 ? [] : [textPart(index === items.length - 1 ? ' and ' : ', ')];
  return [...separator, ...render(item)];
});

const numbersPart = (numbers, color) => textPart(numbers.join(', '), { bold: true, color });

const monthIndex = (row) => Number(row.key.slice(0, 4)) * 12 + Number(row.key.slice(5, 7));

/**
 * The trend sentence under the chart: how many problems are still open and from which months, then which
 * months are fully closed. chart rows are { key: 'YYYY-MM', monthName, year, opened, openProblems }.
 */
export const trendSummary = (chart) => {
  const openMonths = chart.filter((row) => row.openProblems > 0);
  const totalOpen = openMonths.reduce((sum, row) => sum + row.openProblems, 0);
  const spansYears = new Set(chart.map((row) => row.year)).size > 1;
  const monthLabel = (row) => (spansYears ? `${row.monthName} ${row.year}` : row.monthName);
  const parts = [];

  if (totalOpen > 0) {
    parts.push(
      textPart(String(totalOpen), { bold: true }),
      textPart(plural(totalOpen, ' problem is ', ' problems are ')),
      textPart('still open', { bold: true, color: INCIDENT_ACCENT }),
      textPart(' — '),
      ...listParts(openMonths, (row) => [
        textPart(String(row.openProblems), { bold: true }),
        textPart(' from '),
        textPart(monthLabel(row), { bold: true })
      ]),
      textPart('. ')
    );
  }

  // Runs of consecutive months where everything opened that month is closed
  const ranges = [];
  let run = [];
  const flush = () => {
    if (run.length) ranges.push(run);
    run = [];
  };
  chart.forEach((row) => {
    if (!(row.opened > 0 && row.openProblems === 0)) {
      flush();
      return;
    }
    if (run.length && monthIndex(row) - monthIndex(run[run.length - 1]) > 1) flush();
    run.push(row);
  });
  flush();

  if (ranges.length) {
    parts.push(
      ...listParts(ranges, (rows) => [
        textPart(monthLabel(rows[0]), { bold: true }),
        ...(rows.length > 1 ? [textPart(' through '), textPart(monthLabel(rows[rows.length - 1]), { bold: true })] : [])
      ]),
      textPart(` ${ranges.length === 1 && ranges[0].length === 1 ? 'is' : 'are'} fully closed.`)
    );
  }
  return { parts };
};

const numbersOf = (problems) => problems.map((problem) => problem.problemNumber).filter(Boolean).sort();

/**
 * The bullets for one month. month = { problems, closedThisMonthProblems, earlierOpenByMonth, opened, year };
 * each problem has isOpen, major and problemNumber. Each problem number is listed once per bullet.
 */
export const monthPoints = (month) => {
  const problems = month.problems || [];
  const majorNumbers = numbersOf(problems.filter((problem) => problem.major));
  const nonMajorNumbers = numbersOf(problems.filter((problem) => !problem.major));
  const closedThisMonthProblems = month.closedThisMonthProblems || [];
  const closedThisMonthNumbers = numbersOf(closedThisMonthProblems);

  const classClauses = [
    majorNumbers.length && { count: majorNumbers.length, label: 'Major', numbers: majorNumbers, color: INCIDENT_ACCENT },
    nonMajorNumbers.length && { count: nonMajorNumbers.length, label: 'Non-major', numbers: nonMajorNumbers, color: DEEP_GREEN }
  ].filter(Boolean);

  const points = [];
  if (month.opened > 0) {
    points.push({
      parts: [
        textPart(String(month.opened), { bold: true }),
        textPart(` ${plural(month.opened, 'problem was', 'problems were')} `),
        textPart('opened', { bold: true }),
        textPart(': '),
        ...listParts(classClauses, (clause) => [
          textPart(String(clause.count), { bold: true }),
          textPart(' '),
          textPart(clause.label, { bold: true, color: clause.color }),
          textPart(' ('),
          numbersPart(clause.numbers),
          textPart(')')
        ]),
        textPart('.')
      ]
    });
  }

  if (closedThisMonthNumbers.length) {
    points.push({
      parts: [
        textPart(String(closedThisMonthNumbers.length), { bold: true }),
        textPart(` ${plural(closedThisMonthNumbers.length, 'problem was', 'problems were')} `),
        textPart('closed', { bold: true }),
        textPart(' this month ('),
        numbersPart(closedThisMonthNumbers),
        textPart(').')
      ]
    });
  }

  const earlier = month.earlierOpenByMonth || [];
  const earlierCount = earlier.reduce((sum, bucket) => sum + bucket.numbers.length, 0);
  if (earlierCount > 0) {
    const showYear = earlier.some((bucket) => bucket.year !== month.year);
    points.push({
      parts: [
        textPart(String(earlierCount), { bold: true }),
        textPart(` ${plural(earlierCount, 'problem from an earlier month is', 'problems from earlier months are')} `),
        textPart('still open', { bold: true, color: INCIDENT_ACCENT }),
        textPart(': '),
        ...listParts(earlier, (bucket) => [
          textPart(showYear ? `${bucket.label} ${bucket.year}` : bucket.label, { bold: true }),
          textPart(' ('),
          numbersPart(bucket.numbers, INCIDENT_ACCENT),
          textPart(')')
        ]),
        textPart('.')
      ]
    });
  }
  return points;
};

// ---------- notes for open problems ----------

/** Problem statements sometimes carry "**TAG**" markers from ticket templates. */
const cleanTitle = (title) => String(title || '').replace(/\*\*[^*]*\*\*/g, '').replace(/\s+/g, ' ').trim();

/** Fix notes often begin with the template heading "Permanent Fix:", which adds nothing under "Progress made". */
const withoutHeading = (text) => String(text || '').replace(/^\s*permanent fix\b\s*[:\-–]?\s*/i, '');

const asSentence = (text) => {
  const trimmed = String(text || '').trim();
  if (!trimmed) return '';
  return /[.!?]$/.test(trimmed) ? trimmed : `${trimmed}.`;
};

/** UTC, matching the month buckets, so a date reads the same for every viewer. */
export const formatUtcDate = (value) => {
  const date = value ? new Date(value) : null;
  return date && !Number.isNaN(date.getTime())
    ? date.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' })
    : '';
};

/** Used only when a problem has no assignment group. */
const DEFAULT_OWNER = 'Our engineering team';

/**
 * What to say for each ServiceNow problem state.
 *   stage: the short label for where the problem is. meaning: what that stage means for the reader.
 *   action: the next step as a verb phrase ("<owner> to <action>."). null means no next step.
 * The state is matched on its letters only ("Root Cause Analysis" -> rootcauseanalysis), so spacing and case
 * changes in ServiceNow do not matter. A state that is not listed shows the state itself, so a renamed or new
 * state still reads correctly instead of showing wrong text.
 * Resolved and Closed are not listed: those problems are stored as not open, and this note is only built for
 * open problems.
 */
const STATE_COPY = {
  new: { stage: 'Review not started', meaning: 'The team has not begun looking at it.', action: 'start the review' },
  assess: { stage: 'Under review', meaning: 'The team is deciding what caused it.', action: 'complete the review' },
  rootcauseanalysis: { stage: 'Finding the cause', meaning: 'Root cause analysis is in progress.', action: 'confirm the cause' },
  knownerror: {
    stage: 'Cause known, fix pending',
    meaning: 'We know why it happened. The permanent fix is still to be delivered.',
    action: 'deliver the permanent fix'
  },
  fixinprogress: { stage: 'Fix in progress', meaning: 'The fix is being built or rolled out.', action: 'complete the fix' }
};

/** Once under the page title: what this report covers vs Major Incidents. */
export const PAGE_SCOPE_NOTE = 'This page reports Problems and their Permanent fixes.';

/** What Major and Non-major mean on this report. One source for the page and the PowerPoint. */
export const MAJOR_DEFINITION = {
  parts: [
    textPart('A problem is '),
    textPart('Major', { bold: true, color: INCIDENT_ACCENT }),
    textPart(' when the team marks it Major in ServiceNow. Otherwise it is '),
    textPart('Non-major', { bold: true, color: DEEP_GREEN }),
    textPart('. In practice SRE decides this from the impact and how many customers are affected.')
  ]
};
export const PRIORITY_NOTE = 'Priority comes from the linked incident. It is set from how widespread the impact is and the '
  + "product's tier: for example, widespread impact on a Tier 1 product is P1 Critical, and minor impact on a Tier 4 product is P4 Low. "
  + 'SRE decides P1 and P2 together with the Major Incident team. '
  + 'A P1 is not always a Major Incident, and a Major Incident is not always P1.';

/** What Impact (hours) and Impact (days) mean on the problem table. */
export const IMPACT_NOTE = {
  parts: [
    textPart('Impact (hours)', { bold: true, color: MUTED }),
    textPart(' and ', { color: MUTED }),
    textPart('Impact (days)', { bold: true, color: MUTED }),
    textPart(' are the calendar duration of the representative linked incident (how long that incident ran). '
      + 'They are not a count of customers affected.', { color: MUTED })
  ]
};

const stateKey = (state) => String(state || '').toLowerCase().replace(/[^a-z]/g, '');

/** { status, next } for a state and the group (and person, when set) that owns the next step. next is '' when there is nothing to do. */
const copyFor = (state, team) => {
  const owner = String(team || '').trim() || DEFAULT_OWNER;
  const entry = STATE_COPY[stateKey(state)];
  if (!entry) return { status: `${String(state || '').trim() || 'No state set'}.`, next: `${owner} owns the next step.` };
  return { status: `${entry.stage}. ${entry.meaning}`, next: entry.action ? `${owner} to ${entry.action}.` : '' };
};

/**
 * One sentence above the chart: how many problems are open.
 * facts = the open problems. Counts only.
 */
export const openSummary = (facts) => {
  const open = facts.filter((fact) => fact.open);
  if (!open.length) return { parts: [textPart('No problems are open.')] };
  return {
    parts: [
      textPart(String(open.length), { bold: true }),
      textPart(`${plural(open.length, ' problem is ', ' problems are ')}open.`)
    ]
  };
};

/**
 * The note for one open problem, as data: { number, lines: [{ label, text, color }] }.
 * fact = { number, title, state, teamName, assignedTo, rca, workaround, fixNotes, dueAt }.
 * A line is included only when ServiceNow has the text. Initiative and customer benefit are never stored,
 * so they are not shown.
 */
export const problemNote = (fact) => {
  const group = String(fact.teamName || '').trim();
  const person = String(fact.assignedTo || '').trim();
  const owner = group && person ? `${group} (${person})` : group || person;
  const { status, next } = copyFor(fact.state, owner);
  const line = (label, text, color = INK) => ({ label, text, color });
  const addressed = asSentence(forReaders(cleanTitle(fact.title)));
  const cause = asSentence(forReaders(fact.rca));
  const progress = asSentence(forReaders(withoutHeading(fact.fixNotes)))
    || (fact.workaround ? `A workaround is recorded. ${asSentence(forReaders(fact.workaround))}` : '');
  const due = formatUtcDate(fact.dueAt);
  const lines = [
    addressed && line('Problem being addressed', addressed),
    cause && line('Root cause or risk', cause),
    status && line('Current status', status),
    progress && line('Progress made', progress),
    due && line('Expected outcome', `Target ${due}.`),
    next && line('Next step', next, DEEP_GREEN)
  ].filter(Boolean);
  return { number: fact.number, lines };
};
